import { FC, useCallback, useEffect, useMemo, useState } from 'react'

import { FocusNode } from '@please/lrud'

import { Button } from '@/components/Button/Button'
import { focusIds } from '@/constants/focus'
import { UnlockWithCreditButtonProps } from '@/containers/EventDetailPage/Buttons/types'
import { useCommonTranslation } from '@/i18n/useCommonTranslation'
import { ApiService } from '@/service'
import { usePPVPopupStore } from '@/store/usePPVPopupStore'
import useStore from '@/store/useStore'
import { OfferType } from '@/types/servicesTypes/configTypes'

export const UnlockWithCreditButton: FC<UnlockWithCreditButtonProps> = ({
  cost,
  event,
  eventChannel,
  parentFocusId,
}) => {
  const { t } = useCommonTranslation()
  const setPpvPopup = usePPVPopupStore((state) => state.setPpvPopup)
  const setPaymentPopup = useStore((state) => state.common.setPaymentPopup)
  const [offers, setOffers] = useState<Nullable<OfferType[]>>(null)

  const focusId = `${focusIds.EVENT_PAGE}_${focusIds.UNLOCK_WITH_CREDIT}`

  useEffect(() => {
    if (event) {
      getOffers()
    }
    return () => setOffers(null)
  }, [event, eventChannel])

  const getOffers = useCallback(async () => {
    const accessRightIds = (eventChannel?.accessRightIds || [])?.concat(
      event?.accessRightIds || [],
    )
    let offers: OfferType[] = []
    if (accessRightIds.length) {
      try {
        offers = await ApiService.getOffers(accessRightIds)
      } catch (e) {}
    }
    setOffers(offers)
  }, [event, eventChannel])

  const paymentByCreditsOnly = useMemo(() => {
    const hasPpvInfo = event.ppvInfo != null
    const hasSubscription = offers?.length
    return hasPpvInfo && !hasSubscription
  }, [event, offers])

  const onSelectedUnlockPremium = (e: LRUDEvent) => {
    e.preventDefault()
    e.stopPropagation()
    paymentByCreditsOnly
      ? setPpvPopup({ event, eventChannel, prevFocusId: parentFocusId })
      : setPaymentPopup({
          event,
          eventChannel,
          offers,
          prevFocusId: parentFocusId,
        })
  }

  return (
    <FocusNode>
      {offers && (
        <Button
          text={`${t('detailPage.watchForSamsungCredits', {
            cost,
            count: event.ppvInfo?.[0].cost,
          })}`}
          focusId={focusId}
          onClick={onSelectedUnlockPremium}
        />
      )}
    </FocusNode>
  )
}
